/**
 * B-653 (kontrola trzeźwości, etap E1) — okno alarmu o pozytywnej kontroli trzeźwości albo odmowie badania,
 * zamontowane w układzie panelu (`AppLayout`) obok okna CEPiK (wzór: `features/admin/cepik/CepikAlertPopup.tsx`).
 *
 * Kto widzi: serwer (`GET /sobriety-alerts/popup`) oddaje otwarte alarmy KRYTYCZNE tylko odbiorcy — ADMIN albo rola
 * z `sobriety.alert`; pozostałym pustą listę albo 403. Klient NIE zgaduje uprawnień: 403 i 404 (starszy backend bez
 * modułu) traktuje jak „brak alarmów” — cicho, bez toastu, bez ponawiania.
 *
 * Co robi:
 *   • sprawdza co 30 s (i po powrocie do karty), najstarszy alarm pierwszy, przy kilku — licznik „1 z N”;
 *   • „Przyjmuję” wymaga notatki (≥ 3 znaki — ten sam próg co serwer, który i tak go sprawdza i zwraca 400
 *     z komunikatem „Wpisz, co zrobiono…”); przyjęcie przez jedną osobę zamyka okno u wszystkich odbiorców;
 *   • „Odłóż 5 min” chowa TEN alarm w tej karcie przeglądarki (sessionStorage) — nie zamyka go, kolejne alarmy
 *     dalej się pokazują; „Otwórz dokument” odkłada alarm i przechodzi do `/forms/<submissionId>` (okno modalne
 *     zasłoniłoby dokument, a baner w dokumencie pokazuje, że alarm czeka na przyjęcie).
 *
 * ⚠️ Treść okna to dane o zdrowiu (wynik badania) — serwer oddaje je wyłącznie odbiorcom alarmu, więc nie
 * maskujemy tu niczego po stronie klienta.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, Clock, ExternalLink, Loader2, Wine } from 'lucide-react';
import { api } from '@/shared/lib/api';
import { formatDateTime } from '@/shared/lib/utils';
import { useAuthStore } from '@store/auth.store';
import { rozbierzGodzineBadania } from '@/shared/lib/znacznikTrzezwosci';

/** Kształt z kontraktu B-653 (`GET /sobriety-alerts/popup`). */
export interface AlarmTrzezwosci {
  id: string;
  severity: 'CRITICAL' | 'INFO';
  outcome: 'POSITIVE' | 'REFUSED';
  measuredAt: string | null;
  createdAt: string;
  submissionId: string;
  formNumber: string | null;
  formName: string;
  linia: string | null;
  brygada: string | null;
  pojazd: string | null;
  miejsce: string | null;
  badal: 'INSPECTOR' | 'POLICE' | null;
  odczyt: string | null;
  osoba: { imieNazwisko: string | null; numerSluzbowy: string | null } | null;
  instruktor: { imieNazwisko: string | null; telefon: string | null } | null;
  notes: Array<{ at: string; text: string }>;
}

const KLUCZ_ODLOZENIA = 'zur-sobriety-popup-odlozone';
const ODLOZENIE_MS = 5 * 60_000;
const MIN_NOTATKA = 3;

/** Odłożone alarmy tej karty: `{ [id]: do kiedy (ms) }`. Prywatne okno bez sessionStorage — po prostu nic nie pamięta. */
function wczytajOdlozone(): Record<string, number> {
  try {
    const v = JSON.parse(sessionStorage.getItem(KLUCZ_ODLOZENIA) ?? '{}');
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
  } catch { return {}; }
}

/** 403 (rola spoza odbiorców) i 404 (backend bez modułu) = „brak alarmów”, bez hałasu. */
async function pobierzAlarmy(): Promise<AlarmTrzezwosci[]> {
  try {
    const r = await api.get<AlarmTrzezwosci[]>('/sobriety-alerts/popup');
    return Array.isArray(r) ? r.filter((a) => !!a && typeof a === 'object' && typeof a.id === 'string') : [];
  } catch (e: any) {
    const s = e?.response?.status;
    if (s === 403 || s === 404) return [];
    throw e;
  }
}

const komunikatBledu = (e: any, zapas: string): string => {
  const m = e?.response?.data?.message;
  return Array.isArray(m) ? m.join(' ') : typeof m === 'string' && m ? m : zapas;
};

const zLacznikiem = (czesci: Array<string | null | undefined>, lacznik = ' · ') =>
  czesci.filter((x) => typeof x === 'string' && x.trim()).join(lacznik);

function Wiersz({ etykieta, children }: { etykieta: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_1fr] gap-3 px-3 py-2 text-sm">
      <dt className="text-xs font-semibold text-muted-foreground">{etykieta}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </div>
  );
}

export function SobrietyAlertPopup() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.accessToken);
  const [odlozone, setOdlozone] = useState<Record<string, number>>(wczytajOdlozone);
  const [teraz, setTeraz] = useState(Date.now());
  /* Zegar co 15 s — odłożony alarm wraca najpóźniej kilkanaście sekund po upływie 5 minut. */
  useEffect(() => {
    const t = setInterval(() => setTeraz(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  const q = useQuery({
    queryKey: ['sobriety-alerts', 'popup'],
    queryFn: pobierzAlarmy,
    /* Tylko zalogowany panel (AppLayout montuje się po zalogowaniu; token sprawdzamy, żeby nie strzelać przy wylogowaniu). */
    enabled: !!user && !!token,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const wszystkie = q.data ?? [];
  const widoczne = useMemo(() => wszystkie.filter((a) => !(odlozone[a.id] > teraz)), [wszystkie, odlozone, teraz]);
  const a = widoczne[0];

  const [notatka, setNotatka] = useState('');
  const [zajety, setZajety] = useState(false);
  useEffect(() => { setNotatka(''); }, [a?.id]);

  if (!a) return null;

  const odloz = (id: string) => {
    const nowe = { ...wczytajOdlozone(), [id]: Date.now() + ODLOZENIE_MS };
    /* Sprzątanie wygasłych wpisów — mapa nie rośnie w nieskończoność w długo otwartej karcie. */
    for (const [k, v] of Object.entries(nowe)) if (!(v > Date.now())) delete nowe[k];
    try { sessionStorage.setItem(KLUCZ_ODLOZENIA, JSON.stringify(nowe)); } catch { /* prywatne okno */ }
    setOdlozone(nowe);
  };

  const przyjmij = async () => {
    setZajety(true);
    try {
      await api.post(`/sobriety-alerts/${a.id}/ack`, { note: notatka.trim() });
      toast.success('Alarm przyjęty — zamknięty dla wszystkich odbiorców');
      qc.invalidateQueries({ queryKey: ['sobriety-alerts'] });
      /* Baner w dokumencie i panel alertów Pulpitu pokazują stan alarmu — odświeżamy je razem z oknem. */
      qc.invalidateQueries({ queryKey: ['form-submission', a.submissionId] });
      qc.invalidateQueries({ queryKey: ['analytics', 'dashboard-alerts'] });
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      toast.error(komunikatBledu(e, 'Nie udało się przyjąć alarmu'));
    } finally { setZajety(false); }
  };

  const odmowa = a.outcome === 'REFUSED';
  const g = rozbierzGodzineBadania(a.measuredAt);
  const kto = a.badal === 'POLICE' ? 'badała Policja' : a.badal === 'INSPECTOR' ? 'badał inspektor' : null;
  const osoba = zLacznikiem([a.osoba?.imieNazwisko, a.osoba?.numerSluzbowy ? `nr sł. ${a.osoba.numerSluzbowy}` : null]);
  const linia = zLacznikiem([
    zLacznikiem([a.linia ? `linia ${a.linia}` : null, a.brygada ? `brygada ${a.brygada}` : null], ' / '),
    a.pojazd ? `wóz ${a.pojazd}` : null,
  ]);
  const dopiski = Array.isArray(a.notes) ? a.notes.filter((n) => !!n && typeof n === 'object' && n.text) : [];
  const gotowa = notatka.trim().length >= MIN_NOTATKA;

  return (
    <div className="fixed inset-0 z-[4010] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      role="alertdialog" aria-modal="true" aria-labelledby="sobriety-alert-title" data-okno-alarmu-trzezwosci>
      <div className="flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-2xl border-2 border-rose-600 bg-background shadow-2xl">
        <div className="flex shrink-0 items-center gap-3 bg-rose-600 px-5 py-3.5 text-white">
          <Wine className="h-6 w-6 shrink-0" aria-hidden />
          <div className="min-w-0">
            <p id="sobriety-alert-title" className="font-bold">
              {odmowa ? 'Odmowa badania trzeźwości' : 'Pozytywna kontrola trzeźwości'}
            </p>
            <p className="truncate text-xs text-white/85">
              {zLacznikiem([a.formNumber ?? a.formName, `zgłoszono ${formatDateTime(a.createdAt)}`])}
            </p>
          </div>
          {widoczne.length > 1 && (
            <span className="ml-auto shrink-0 rounded-full bg-white/20 px-2 py-0.5 text-xs font-bold tabular-nums">
              1 z {widoczne.length}
            </span>
          )}
        </div>

        <div className="min-h-0 space-y-3 overflow-y-auto px-5 py-4">
          {a.formNumber && <p className="text-sm font-semibold">{a.formName}</p>}
          <dl className="divide-y rounded-xl border">
            <Wiersz etykieta="Godzina badania">
              {g ? (
                <span className="flex items-baseline gap-2">
                  <span className="text-2xl font-black tabular-nums text-rose-600 dark:text-rose-400">{g.godzina}</span>
                  <span className="text-xs text-muted-foreground">{g.data}</span>
                </span>
              ) : (
                <span className="font-bold text-rose-600 dark:text-rose-400">godz. nieznana</span>
              )}
            </Wiersz>
            <Wiersz etykieta={odmowa ? 'Wynik' : 'Odczyt'}>
              <span className="font-semibold">{odmowa ? 'odmowa poddania się badaniu' : a.odczyt || '—'}</span>
              {kto && <span className="text-muted-foreground"> · {kto}</span>}
            </Wiersz>
            <Wiersz etykieta="Badany">{osoba || '—'}</Wiersz>
            <Wiersz etykieta="Linia / brygada · wóz">{linia || '—'}</Wiersz>
            <Wiersz etykieta="Miejsce">{a.miejsce || '—'}</Wiersz>
            <Wiersz etykieta="Instruktor">
              {a.instruktor?.imieNazwisko || '—'}
              {/* Telefon jako zwykły tekst do zaznaczenia i skopiowania (dyspozytor dzwoni z aparatu, nie z przeglądarki). */}
              {a.instruktor?.telefon && <> · <span className="select-all font-mono tabular-nums">{a.instruktor.telefon}</span></>}
            </Wiersz>
          </dl>

          {dopiski.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] dark:border-amber-500/30 dark:bg-amber-500/10">
              <p className="mb-1 text-xs font-bold text-amber-800 dark:text-amber-200">Dopiski</p>
              <ul className="space-y-0.5">
                {dopiski.map((n, i) => (
                  <li key={i}><span className="tabular-nums text-muted-foreground">{formatDateTime(n.at)}</span> — {n.text}</li>
                ))}
              </ul>
            </div>
          )}

          <textarea value={notatka} onChange={(e) => setNotatka(e.target.value)} rows={2} autoFocus
            aria-label="Co zrobiono"
            placeholder="Co zrobiono, np. kierowca odsunięty od służby, powiadomiony kierownik, zastępstwo: …"
            className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-400/40" />
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button type="button" onClick={() => odloz(a.id)}
              className="mr-auto inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground">
              <Clock className="h-3.5 w-3.5" /> Odłóż 5 min
            </button>
            <button type="button" onClick={() => { odloz(a.id); navigate(`/forms/${a.submissionId}`); }}
              title="Okno wróci za 5 minut, jeśli alarm nie zostanie przyjęty"
              className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium hover:bg-muted">
              <ExternalLink className="h-4 w-4" /> Otwórz dokument
            </button>
            <button type="button" onClick={przyjmij} disabled={zajety || !gotowa}
              title={gotowa ? undefined : `Wpisz, co zrobiono (co najmniej ${MIN_NOTATKA} znaki)`}
              className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-2 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-40">
              {zajety ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Przyjmuję
            </button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Przyjęcie zamyka alarm dla wszystkich odbiorców — pozostali zobaczą w dokumencie, kto go przyjął i co zrobiono.
            {widoczne.length > 1 ? ` Czeka jeszcze ${widoczne.length - 1} ${widoczne.length - 1 === 1 ? 'alarm' : widoczne.length - 1 < 5 ? 'alarmy' : 'alarmów'}.` : ''}
          </p>
        </div>
      </div>
    </div>
  );
}
